import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Activity, ShieldCheck, Database, AlertTriangle, Link2, Map as MapIcon, FileCheck, Sparkles, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, KpiCard, Panel, btnPrimary, useAdmin } from "@/components/admin/ui";

const STATUS = {
  healthy: { dot: "bg-[#2E7D46]", label: "Healthy", text: "text-[#2E7D46]" },
  attention: { dot: "bg-[#B04A3A]", label: "Needs attention", text: "text-[#B04A3A]" },
  monitoring: { dot: "bg-[#B7761F]", label: "Monitoring", text: "text-[#B7761F]" },
  no_source: { dot: "bg-[#9A958B]", label: "No source", text: "text-[#5C6B61]" },
};
const label = (s) => (s || "").replace(/_/g, " ");

export default function AdminDataHealth() {
  const { admin } = useAdmin();
  const canView = admin?.permissions?.includes("states");
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => { if (canView) api.adminDataHealth().then(setData).catch(() => setData(null)); }, [canView]);
  useEffect(() => { load(); }, [load]);
  const [scanning, setScanning] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showCollect, setShowCollect] = useState(false);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await api.adminDataAiRun({ limit: 20 });
      toast.success(`Scan done — ${r.sources_checked} checked, ${r.baselined} baselined, ${r.queued} flagged`, { duration: 8000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); } finally { setScanning(false); }
  };

  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>;
  const n = data.national;
  const rows = filter ? data.states.filter((s) => s.status === filter) : data.states;
  const statusCounts = data.states.reduce((a, s) => ({ ...a, [s.status]: (a[s.status] || 0) + 1 }), {});

  return (
    <div className="p-6 md:p-8" data-testid="admin-data-health">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div><span className={OVERLINE}>HomeRange Data AI</span><h1 className={`${H1} mt-1`}>Data Health</h1></div>
        {canView && (
          <div className="flex gap-2">
            <button onClick={() => setShowCollect(true)} className="inline-flex items-center gap-2 border border-[#E5E2DC] px-4 py-2 text-sm font-semibold text-[#1F2A22] hover:border-[#1F2A22]" data-testid="collect-open"><Sparkles size={15} /> Collect with AI</button>
            <button onClick={() => setShowImport(true)} className="inline-flex items-center gap-2 border border-[#E5E2DC] px-4 py-2 text-sm font-semibold text-[#1F2A22] hover:border-[#1F2A22]" data-testid="import-open"><Upload size={15} /> Import data</button>
            <button onClick={runScan} disabled={scanning} className={btnPrimary} data-testid="run-scan">{scanning ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Run AI scan</button>
          </div>
        )}
      </div>
      <p className="text-sm text-[#5C6B61] mt-2 max-w-2xl">National coverage of official state hunting data. Verified draw data publishes to the customer explorer; states still being ingested show as monitoring.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiCard label="States connected" value={`${n.states_connected} / ${n.states_total}`} icon={MapIcon} testid="dh-connected" />
        <KpiCard label="Current regulations" value={`${n.regs_current} / ${n.states_total}`} icon={FileCheck} testid="dh-regs" />
        <KpiCard label="Verified draw data" value={`${n.draw_data_states} / ${n.states_total}`} icon={Database} accent testid="dh-draw" />
        <KpiCard label="Sources healthy" value={`${n.sources_healthy_pct}%`} icon={ShieldCheck} testid="dh-healthy" />
        <KpiCard label="Records verified" value={n.records_verified.toLocaleString()} icon={FileCheck} testid="dh-records" />
        <KpiCard label="Source links" value={n.sources_total} icon={Link2} testid="dh-sources" />
        <KpiCard label="Source errors" value={n.source_errors} icon={AlertTriangle} testid="dh-errors" />
        <Link to="/admin/review-queue" data-testid="dh-review-link"><KpiCard label="Needs review" value={n.needs_review} icon={Activity} accent={n.needs_review > 0} testid="dh-review" /></Link>
        <KpiCard label="Planner gaps" value={n.open_discrepancies ?? 0} icon={AlertTriangle} accent={(n.open_discrepancies ?? 0) > 0} testid="dh-discrepancies" />
      </div>

      <Panel title="State source status" className="mt-6">
        <div className="flex flex-wrap gap-2 mb-4" data-testid="dh-filters">
          <button onClick={() => setFilter("")} className={`px-3 py-1.5 text-xs border ${filter === "" ? "bg-[#1F2A22] text-white border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid="dh-filter-all">All ({data.states.length})</button>
          {Object.keys(STATUS).map((st) => (
            <button key={st} onClick={() => setFilter(st)} className={`px-3 py-1.5 text-xs border capitalize ${filter === st ? "bg-[#1F2A22] text-white border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61] hover:border-[#1F2A22]"}`} data-testid={`dh-filter-${st}`}>
              {STATUS[st].label} ({statusCounts[st] || 0})
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#5C6B61] border-b border-[#E5E2DC]">
              <th className="px-3 py-2 font-serif">State</th><th className="px-3 py-2 font-serif">Draw system</th><th className="px-3 py-2 font-serif">Sources</th><th className="px-3 py-2 font-serif">Regs</th><th className="px-3 py-2 font-serif">Draw records</th><th className="px-3 py-2 font-serif">Last check</th><th className="px-3 py-2 font-serif">Status</th>
            </tr></thead>
            <tbody>
              {rows.map((s) => {
                const st = STATUS[s.status] || STATUS.no_source;
                return (
                  <tr key={s.state} className="border-b border-[#E5E2DC] last:border-0" data-testid={`dh-row-${s.state}`}>
                    <td className="px-3 py-3 text-sm text-[#1F2A22] font-medium">{s.state}</td>
                    <td className="px-3 py-3 text-xs text-[#5C6B61]">{s.system_label}</td>
                    <td className="px-3 py-3 text-sm tabular-nums"><span className={s.source_errors ? "text-[#B04A3A]" : "text-[#1F2A22]"}>{s.sources_healthy}/{s.sources_connected}</span></td>
                    <td className="px-3 py-3 text-sm">{s.has_regs ? <ShieldCheck size={15} className="text-[#2E7D46]" /> : "—"}</td>
                    <td className="px-3 py-3 text-sm text-[#1F2A22] tabular-nums">{s.draw_records || "—"}</td>
                    <td className="px-3 py-3 text-xs text-[#5C6B61]">{s.last_check ? new Date(s.last_check).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-3"><span className="inline-flex items-center gap-1.5 text-xs font-medium"><span className={`w-2 h-2 rounded-full ${st.dot}`} /><span className={st.text}>{st.label}</span></span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
      <DiscrepancyPanel canView={canView} onChange={load} />
      <SpecialDrawsAdminPanel canView={canView} />
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={load} />}
      {showCollect && <CollectModal states={data.states} onClose={() => setShowCollect(false)} onDone={load} />}
    </div>
  );
}

const SD_FIELDS = [
  ["program_name", "Program name"], ["program_url", "Official program URL"],
  ["ticket_price", "Ticket price ($)"], ["ticket_bundle", "Bundle / fee note"],
  ["application_window", "Application window"], ["purchase_deadline", "Purchase deadline (YYYY-MM-DD)"],
  ["application_deadline", "Application deadline (YYYY-MM-DD)"], ["drawing_date", "Drawing date (YYYY-MM-DD)"],
  ["prizes_note", "Prizes / tags note"], ["eligibility_note", "Eligibility note"],
  ["oil_note", "OIL rule note"], ["special_fee_note", "Special pool fee note"],
  ["verification_note", "Verification note"], ["source_url", "Source URL"], ["source_agency", "Source agency"],
];

function SpecialDrawsAdminPanel({ canView }) {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const load = useCallback(() => { if (canView) api.adminSpecialDraws().then((d) => setRows(d.special_draws || [])).catch(() => setRows([])); }, [canView]);
  useEffect(() => { load(); }, [load]);

  const openEdit = (r) => {
    setForm(Object.fromEntries(SD_FIELDS.map(([k]) => [k, r[k] ?? ""])));
    setEdit(r);
  };
  const save = async () => {
    const payload = {};
    for (const [k] of SD_FIELDS) {
      const v = form[k];
      if (v === "" || v == null) { payload[k] = null; continue; }
      payload[k] = k === "ticket_price" ? (isNaN(Number(v)) ? v : Number(v)) : v;
    }
    payload.confidence = form.confidence || edit.confidence;
    try { await api.adminSpecialUpdate(edit.id, payload); toast.success("Saved"); setEdit(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.program_name}"?`)) return;
    try { await api.adminSpecialDelete(r.id); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  if (!rows) return null;
  const inp = "w-full border border-[#E5E2DC] bg-white px-2.5 py-1.5 text-sm outline-none focus:border-[#1F2A22]";
  return (
    <Panel title={`Special draws & raffles (${rows.length})`} className="mt-6" data-testid="special-draws-admin">
      <p className="text-xs text-[#5C6B61] mb-4 max-w-2xl">Raffles, Super Tags, once-in-a-lifetime rules and premium pools shown in the Planner, state pages and HomeRange AI. Verified records carry an official source URL; anything else is marked needs-review. Re-importing the same program never overwrites a record you've edited.</p>
      {rows.length === 0 ? (
        <p className="text-sm text-[#5C6B61] py-4" data-testid="special-admin-empty">No special-draw records yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#5C6B61] border-b border-[#E5E2DC]">
              <th className="px-3 py-2 font-serif">State</th><th className="px-3 py-2 font-serif">Program</th><th className="px-3 py-2 font-serif">Type</th>
              <th className="px-3 py-2 font-serif">Deadline</th><th className="px-3 py-2 font-serif">Status</th><th className="px-3 py-2 font-serif">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#E5E2DC] last:border-0" data-testid={`special-admin-row-${r.id}`}>
                  <td className="px-3 py-3 text-sm text-[#1F2A22] font-medium">{r.jurisdiction}</td>
                  <td className="px-3 py-3 text-sm text-[#1F2A22]">{r.program_name}{r.managed && <span className="ml-1.5 text-[10px] text-[#B7761F] font-semibold">edited</span>}</td>
                  <td className="px-3 py-3 text-xs text-[#5C6B61]">{r.type_label}</td>
                  <td className="px-3 py-3 text-xs tabular-nums text-[#1F2A22]">{r.next_deadline || "—"} <span className="text-[#5C6B61]">({r.deadline_status})</span></td>
                  <td className="px-3 py-3">{r.confidence === "verified"
                    ? <span className="text-[11px] font-semibold text-[#2E7D46]">verified</span>
                    : <span className="text-[11px] font-semibold text-[#B7761F]">needs review</span>}</td>
                  <td className="px-3 py-3 flex gap-2">
                    <button onClick={() => openEdit(r)} className="text-xs font-semibold text-[#1F2A22] hover:underline" data-testid={`special-edit-${r.id}`}>Edit</button>
                    <button onClick={() => del(r)} className="text-xs font-semibold text-[#B04A3A] hover:underline" data-testid={`special-delete-${r.id}`}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-[80] bg-black/40 grid place-items-center p-4" onClick={() => setEdit(null)} data-testid="special-edit-modal">
          <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-[#1F2A22]">Edit — {edit.program_name}</h3>
              <button onClick={() => setEdit(null)} aria-label="Close" data-testid="special-edit-close"><X size={18} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {SD_FIELDS.map(([k, lbl]) => (
                <label key={k} className="block">
                  <span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">{lbl}</span>
                  <input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inp} data-testid={`special-form-${k}`} />
                </label>
              ))}
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Confidence</span>
                <select value={form.confidence || edit.confidence} onChange={(e) => setForm({ ...form, confidence: e.target.value })} className={inp} data-testid="special-form-confidence">
                  <option value="verified">verified</option>
                  <option value="needs_review">needs_review</option>
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEdit(null)} className="border border-[#E5E2DC] px-4 py-2 text-sm font-semibold text-[#5C6B61]">Cancel</button>
              <button onClick={save} className={btnPrimary} data-testid="special-edit-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

function DiscrepancyPanel({ canView, onChange }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { if (canView) api.adminDiscrepancies("open").then((d) => setRows(d.discrepancies || [])).catch(() => setRows([])); }, [canView]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => {
    try { await api.adminResolveDiscrepancy(id, action); toast.success(`Marked ${action}d`); load(); onChange?.(); }
    catch { toast.error("Action failed"); }
  };
  if (!rows) return null;
  return (
    <Panel title="Planner data gaps (AI-discovered)" className="mt-6" data-testid="discrepancy-panel">
      <p className="text-xs text-[#5C6B61] mb-4 max-w-2xl">Species &amp; states hunters asked HomeRange AI about that the Points Planner has no verified records for. Prioritise by request volume, then extract via Collect with AI / Import.</p>
      {rows.length === 0 ? (
        <p className="text-sm text-[#5C6B61] py-4" data-testid="discrepancy-empty">No open gaps — the Planner covers everything hunters have asked about. </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#5C6B61] border-b border-[#E5E2DC]">
              <th className="px-3 py-2 font-serif">State</th><th className="px-3 py-2 font-serif">Species</th><th className="px-3 py-2 font-serif">Requests</th><th className="px-3 py-2 font-serif">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-b border-[#E5E2DC] last:border-0" data-testid={`discrepancy-row-${d.id}`}>
                  <td className="px-3 py-3 text-sm text-[#1F2A22] font-medium">{d.state}</td>
                  <td className="px-3 py-3 text-sm text-[#1F2A22]">{d.species}</td>
                  <td className="px-3 py-3 text-sm tabular-nums text-[#B7761F] font-semibold">{d.seen_count}×</td>
                  <td className="px-3 py-3 flex gap-2">
                    <button onClick={() => act(d.id, "resolve")} className="text-xs font-semibold text-[#2E7D46] hover:underline" data-testid={`discrepancy-resolve-${d.id}`}>Resolve</button>
                    <button onClick={() => act(d.id, "dismiss")} className="text-xs font-semibold text-[#5C6B61] hover:underline" data-testid={`discrepancy-dismiss-${d.id}`}>Dismiss</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const split = (l) => l.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g)?.map((c) => c.replace(/^"|"$/g, "").trim()) || [];
  const headers = split(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, "_"));
  const numeric = new Set(["draw_odds_pct", "season_year"]);
  return lines.slice(1).map((line) => {
    const cells = split(line);
    const row = {};
    headers.forEach((h, i) => {
      let v = cells[i];
      if (v !== undefined && numeric.has(h) && v !== "") v = Number(v);
      if (v !== undefined && v !== "") row[h] = v;
    });
    return row;
  });
}

function ImportModal({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [src, setSrc] = useState("");
  const [busy, setBusy] = useState(false);
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const records = parseCsv(String(reader.result));
        if (!records.length) { toast.error("Couldn't parse any rows from that CSV"); return; }
        setText(JSON.stringify(records, null, 2));
        toast.success(`Parsed ${records.length} rows from CSV — review & import`);
      } catch { toast.error("Failed to parse CSV"); }
    };
    reader.readAsText(f);
  };
  const submit = async () => {
    let records;
    try { records = JSON.parse(text); if (!Array.isArray(records)) throw new Error(); }
    catch { toast.error("Records must be a valid JSON array"); return; }
    setBusy(true);
    try {
      const r = await api.adminDataImport({ records, data_year: Number(year) || undefined, source_url: src || undefined });
      toast.success(`Imported ${r.inserted}, skipped ${r.skipped}, flagged ${r.queued}`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); } finally { setBusy(false); }
  };
  const downloadTemplate = async () => {
    try {
      const blob = await api.adminImportTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "homerange_draw_import_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };
  const inp = "w-full border border-[#E5E2DC] bg-white px-3 py-2 mt-1 text-sm outline-none focus:border-[#1F2A22]";
  return (
    <div className="fixed inset-0 z-50" data-testid="import-modal">
      <div className="absolute inset-0 bg-[#1F2A22]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-xl bg-[#F9F8F6] p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-[#1F2A22]">Import verified draw data</h3>
          <button onClick={onClose} aria-label="Close" data-testid="import-close"><X size={20} className="text-[#5C6B61]" /></button>
        </div>
        <p className="text-xs text-[#5C6B61] mb-3">Upload a CSV of official draw results or paste a JSON array. Required columns/keys: jurisdiction, species, unit, weapon, residency, draw_odds_pct. Optional: season, points_required, point_creep, season_year, source_url. Impossible values are routed to the review queue instead of publishing.</p>
        <label className="flex items-center gap-2 text-sm text-[#1F2A22] border border-dashed border-[#C7C2B8] px-3 py-2.5 cursor-pointer hover:border-[#1F2A22] mb-3">
          <Upload size={15} /> Upload CSV file
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="import-csv" />
        </label>
        <button onClick={downloadTemplate} className="text-xs text-[#1F2A22] underline underline-offset-2 hover:text-[#B04A3A] mb-3" data-testid="import-template">Download CSV template</button>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Data year</span><input type="number" value={year} onChange={(e) => setYear(e.target.value)} className={inp} data-testid="import-year" /></label>
          <label className="block"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Source URL</span><input value={src} onChange={(e) => setSrc(e.target.value)} className={inp} placeholder="https://official-state.gov/…" data-testid="import-source" /></label>
        </div>
        <label className="block mt-3"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Records (JSON array)</span>
          <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} className={`${inp} font-mono text-xs`} placeholder='[{"jurisdiction":"Nevada","species":"Elk","unit":"Unit 111","season":"Oct 1-14","weapon":"Rifle","residency":"Resident","points_required":"8 pts","draw_odds_pct":12.5,"point_creep":"+0.5 pts/yr"}]' data-testid="import-records" /></label>
        <button onClick={submit} disabled={busy} className={`${btnPrimary} w-full mt-4`} data-testid="import-submit">{busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Import</button>
      </div>
    </div>
  );
}


function CollectModal({ states, onClose, onDone }) {
  const [state, setState] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [content, setContent] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (!state) { toast.error("Choose a state"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.adminDataAiCollect({
        jurisdiction: state, season_year: Number(year) || undefined,
        content: content.trim() || undefined, url: url.trim() || undefined,
      });
      setResult(r);
      if (r.error) toast.error(`Source unavailable (${r.error}) — paste the page text instead`, { duration: 8000 });
      else toast.success(`Collected ${r.extracted} records → ${r.queued} queued for review`, { duration: 8000 });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Collection failed"); } finally { setBusy(false); }
  };
  const inp = "w-full border border-[#E5E2DC] bg-white px-3 py-2 mt-1 text-sm outline-none focus:border-[#1F2A22]";
  return (
    <div className="fixed inset-0 z-50" data-testid="collect-modal">
      <div className="absolute inset-0 bg-[#1F2A22]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-xl bg-[#F9F8F6] p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-[#1F2A22] inline-flex items-center gap-2"><Sparkles size={18} className="text-[#B7761F]" /> Collect draw data with AI</h3>
          <button onClick={onClose} aria-label="Close" data-testid="collect-close"><X size={20} className="text-[#5C6B61]" /></button>
        </div>
        <p className="text-xs text-[#5C6B61] mb-3">Claude reads an <b>approved official state source</b> and extracts real draw records. Nothing publishes automatically — every extracted record goes to the Review Queue for a human to approve. If the live .gov page is blocked from our server, paste the page text below instead.</p>
        <label className="block"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">State</span>
          <select value={state} onChange={(e) => setState(e.target.value)} className={inp} data-testid="collect-state">
            <option value="">Select state…</option>
            {(states || []).map((s) => <option key={s.state} value={s.state}>{s.state}</option>)}
          </select></label>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <label className="block"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Season year</span><input type="number" value={year} onChange={(e) => setYear(e.target.value)} className={inp} data-testid="collect-year" /></label>
          <label className="block"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Source URL (optional)</span><input value={url} onChange={(e) => setUrl(e.target.value)} className={inp} placeholder="Defaults to the state's official source" data-testid="collect-url" /></label>
        </div>
        <label className="block mt-3"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Paste source text (optional — use when the live page is blocked)</span>
          <textarea rows={7} value={content} onChange={(e) => setContent(e.target.value)} className={`${inp} text-xs`} placeholder="Paste the official draw-results page text here…" data-testid="collect-content" /></label>
        {result && (
          <div className="mt-3 text-sm border border-[#E5E2DC] bg-white px-3 py-2" data-testid="collect-result">
            {result.error
              ? <span className="text-[#B04A3A]">Could not read the live source ({result.error}). Paste the page text and try again.</span>
              : <span className="text-[#1F2A22]">Extracted <b>{result.extracted}</b>, queued <b>{result.queued}</b> for review{result.errors ? `, ${result.errors} flagged` : ""}. Review them in the <Link to="/admin/review-queue" className="underline">Review Queue</Link>.</span>}
          </div>
        )}
        <button onClick={submit} disabled={busy} className={`${btnPrimary} w-full mt-4`} data-testid="collect-submit">{busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Collect &amp; queue for review</button>
      </div>
    </div>
  );
}
